<template>
  <div class="dd-host relative h-screen w-full overflow-hidden bg-[#050508] text-gray-200">
    <iframe
      v-if="showIframe"
      ref="deskFrame"
      class="absolute inset-0 h-full w-full border-0"
      title="3D导演台"
      :src="iframeSrc"
      allow="fullscreen; clipboard-read; clipboard-write"
      @load="onIframeLoad"
    />

    <div
      v-if="showOverlay"
      class="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-[#050508]/92 px-6 text-center"
    >
      <template v-if="loadError">
        <p class="text-sm text-amber-200/95">{{ loadError }}</p>
        <p class="max-w-md text-[11px] leading-relaxed text-gray-500">
          请确认已构建导演台，并<strong class="text-gray-400">重启</strong>
          <code class="rounded bg-white/10 px-1.5 py-0.5 text-gray-300">npm run serve</code>。
          构建命令：
          <code class="rounded bg-white/10 px-1.5 py-0.5 text-gray-300">npm run build:director-desk</code>
          （需 Node ≥ 18）
        </p>
        <div class="mt-2 flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            class="h-8 rounded-lg border border-white/15 bg-white/[0.06] px-3 text-[11px] hover:bg-white/10"
            @click="goBackFromDirector"
          >
            返回
          </button>
          <button
            type="button"
            class="h-8 rounded-lg border border-violet-500/40 bg-violet-600/80 px-3 text-[11px] text-white hover:bg-violet-500/90"
            @click="retryLoad"
          >
            重试加载
          </button>
        </div>
      </template>
      <template v-else-if="busy">
        <i class="fas fa-spinner fa-spin text-lg text-violet-300" aria-hidden="true"></i>
        <p class="text-sm text-gray-300">{{ busy }}</p>
      </template>
      <template v-else>
        <i class="fas fa-spinner fa-spin text-lg text-violet-300" aria-hidden="true"></i>
        <p class="text-sm text-gray-300">正在准备 3D 导演台…</p>
      </template>
    </div>

    <div
      v-if="bridgeReady && !loadError && isOpenedFromNodeGraph"
      class="pointer-events-none absolute left-3 top-3 z-20 max-w-[min(92vw,360px)] rounded-xl border border-white/10 bg-black/55 px-3 py-2 text-[10px] leading-relaxed text-gray-300 backdrop-blur-sm"
    >
      <p class="font-medium text-violet-200/95">已从节点画布打开</p>
      <p class="mt-0.5 text-gray-400">
        在右侧机位面板截图后点「发送到画布」即可回写下游图片节点；点标题栏关闭可返回。
      </p>
    </div>
  </div>
</template>

<script>
import { showErrorToast, showSuccessToast } from '@/utils/toast'
import { uploadVideoImage } from '@/utils/videoImageApi'

const NGC_DIRECTOR_PNG_SESSION_KEY = 'ngc_director_png_v1'
const NGC_LAST_OPENED_CANVAS_KEY = 'ngc_last_opened_canvas'
const NGC_DIRECTOR_RETURN_CTX_KEY = 'ngc_director_return_ctx_v1'
const NGC_DIRECTOR_UPSTREAM_V1 = 'ngc_director_upstream_v1'
const NGC_DIRECTOR_SCENE_V1 = 'ngc_director_scene_v1'

function directorSceneSessionKey(deckId) {
  const id = String(deckId || '').trim()
  return id ? `${NGC_DIRECTOR_SCENE_V1}:${id}` : NGC_DIRECTOR_SCENE_V1
}

/** 与 packages/director-desk 的 Vite base 一致 */
const DIRECTOR_DESK_BASE = '/director-desk/'
const LOAD_TIMEOUT_MS = 20000

export default {
  name: 'DirectorDeck3D',
  data() {
    return {
      showIframe: true,
      iframeLoaded: false,
      loadError: '',
      busy: '',
      bridgeReady: false,
      _boundMessage: null,
      _loadTimer: null,
      _iframeEpoch: 0,
      _hydratedFromNodeSnapshot: false
    }
  },
  computed: {
    isOpenedFromNodeGraph() {
      const q = this.$route.query || {}
      return q.ngc === '1' || q.ngc === 1
    },
    showOverlay() {
      if (this.loadError || this.busy) return true
      // 桥接就绪后收起遮罩；iframe 已 load 也收起（避免 ready 消息丢失时一直转圈）
      if (this.bridgeReady || this.iframeLoaded) return false
      return true
    },
    iframeSrc() {
      const q = new URLSearchParams()
      q.set('theme', 'dark')
      q.set('_', String(this._iframeEpoch || 0))
      const deck = this.queryStr('deck')
      const canvas = this.queryStr('canvas')
      if (deck) q.set('instanceId', deck)
      if (canvas) q.set('canvasId', canvas)
      return `${DIRECTOR_DESK_BASE}index.html?${q.toString()}`
    }
  },
  mounted() {
    this._boundMessage = this.onHostMessage.bind(this)
    window.addEventListener('message', this._boundMessage)
    this.armLoadTimeout()
  },
  beforeDestroy() {
    this.clearLoadTimeout()
    if (this._boundMessage) {
      window.removeEventListener('message', this._boundMessage)
      this._boundMessage = null
    }
  },
  methods: {
    queryStr(key) {
      const q = this.$route.query || {}
      const raw = q[key]
      if (raw == null) return ''
      return String(Array.isArray(raw) ? raw[0] : raw).trim()
    },
    clearLoadTimeout() {
      if (this._loadTimer) {
        clearTimeout(this._loadTimer)
        this._loadTimer = null
      }
    },
    armLoadTimeout() {
      this.clearLoadTimeout()
      this._loadTimer = setTimeout(() => {
        if (this.bridgeReady || this.iframeLoaded) return
        this.loadError =
          '导演台加载超时。开发环境请重启 npm run serve；并确认 public/director-desk/index.html 已存在。'
      }, LOAD_TIMEOUT_MS)
    },
    retryLoad() {
      this.loadError = ''
      this.busy = ''
      this.bridgeReady = false
      this.iframeLoaded = false
      this.showIframe = false
      this._iframeEpoch += 1
      this.$nextTick(() => {
        this.showIframe = true
        this.armLoadTimeout()
      })
    },
    onIframeLoad() {
      this.iframeLoaded = true
      // 若子应用 JS 报错不会发 ready，仍先露出 iframe，便于排查
      this.clearLoadTimeout()
    },
    getFrameWindow() {
      const el = this.$refs.deskFrame
      return el && el.contentWindow ? el.contentWindow : null
    },
    postToDesk(type, payload) {
      const win = this.getFrameWindow()
      if (!win) return
      try {
        win.postMessage({ type, payload: payload || {} }, window.location.origin)
      } catch (e) {
        console.warn('[DirectorDeckHost] postMessage failed', e)
      }
    },
    pushHostSession() {
      const deck = this.queryStr('deck')
      const canvas = this.queryStr('canvas')
      let sceneSnapshot = null
      try {
        const raw =
          sessionStorage.getItem(directorSceneSessionKey(deck)) ||
          sessionStorage.getItem(NGC_DIRECTOR_SCENE_V1) ||
          ''
        if (raw) {
          const o = JSON.parse(raw)
          if (
            o &&
            typeof o === 'object' &&
            o.snapshot &&
            typeof o.snapshot === 'object' &&
            (!deck || !o.deckId || String(o.deckId) === deck)
          ) {
            sceneSnapshot = o.snapshot
          }
        }
      } catch {
        sceneSnapshot = null
      }
      this._hydratedFromNodeSnapshot = !!sceneSnapshot
      this.postToDesk('storyai:director-desk-session', {
        instanceId: deck || null,
        canvasId: canvas || null,
        theme: 'dark',
        sceneSnapshot
      })
    },
    pushHostPanoramaFromUpstream() {
      if (!this.isOpenedFromNodeGraph) return
      // 节点已有完整场景快照时，不再重复注入上游全景，避免冲掉已保存布景
      if (this._hydratedFromNodeSnapshot) return
      const deck = this.queryStr('deck')
      let raw = ''
      try {
        raw = sessionStorage.getItem(NGC_DIRECTOR_UPSTREAM_V1) || ''
      } catch {
        return
      }
      if (!raw) return
      let obj = null
      try {
        obj = JSON.parse(raw)
      } catch {
        return
      }
      if (!obj || (deck && obj.deckId && obj.deckId !== deck) || !Array.isArray(obj.items)) return
      const firstImg = obj.items.find(x => x && x.kind === 'image' && String(x.url || '').trim())
      if (!firstImg) return
      this.postToDesk('storyai:director-desk-panorama', {
        edgeId: String(firstImg.edgeId || 'upstream'),
        sourceNodeId: String(firstImg.sourceNodeId || 'upstream'),
        imageUrl: String(firstImg.url).trim(),
        fileName: String(firstImg.title || '画布上游图.png')
      })
    },
    persistSceneSyncFromDesk(payload) {
      const deck = this.queryStr('deck')
      const instanceId =
        payload && payload.instanceId != null ? String(payload.instanceId).trim() : ''
      const sceneSnapshot = payload && payload.sceneSnapshot
      if (!sceneSnapshot || typeof sceneSnapshot !== 'object') return
      const deckId = instanceId || deck || ''
      if (!deckId) return
      const entry = JSON.stringify({
        deckId,
        snapshot: sceneSnapshot,
        at: Date.now()
      })
      try {
        sessionStorage.setItem(directorSceneSessionKey(deckId), entry)
        sessionStorage.setItem(NGC_DIRECTOR_SCENE_V1, entry)
      } catch (e) {
        console.warn('[DirectorDeckHost] scene snapshot too large for sessionStorage', e)
      }
    },
    onHostMessage(event) {
      if (!event || !event.data || typeof event.data !== 'object') return
      const frame = this.$refs.deskFrame
      const fromDesk = !!(frame && event.source && event.source === frame.contentWindow)
      if (!fromDesk && event.origin !== window.location.origin) return
      const data = event.data
      const type = data.type
      if (type === 'storyai:director-desk-ready') {
        this.bridgeReady = true
        this.iframeLoaded = true
        this.loadError = ''
        this.clearLoadTimeout()
        this.pushHostSession()
        this.$nextTick(() => {
          this.pushHostPanoramaFromUpstream()
        })
        return
      }
      if (type === 'storyai:director-desk-scene-sync') {
        this.persistSceneSyncFromDesk(data.payload || {})
        return
      }
      if (type === 'storyai:director-desk-close') {
        this.goBackFromDirector()
        return
      }
      if (type === 'storyai:director-desk-captures-sent') {
        const captures =
          data.payload && Array.isArray(data.payload.captures) ? data.payload.captures : []
        void this.handleCapturesFromDesk(captures)
        return
      }
    },
    /** 回跳画布：优先用打开导演台时锁定的 return ctx，避免串到其它画布 */
    readDirectorReturnCtx() {
      try {
        const raw = sessionStorage.getItem(NGC_DIRECTOR_RETURN_CTX_KEY) || ''
        if (!raw) return null
        const o = JSON.parse(raw)
        return o && typeof o === 'object' ? o : null
      } catch {
        return null
      }
    },
    resolveReturnCanvasId() {
      const ctx = this.readDirectorReturnCtx()
      const fromCtx = ctx && ctx.canvas != null ? String(ctx.canvas).trim() : ''
      if (fromCtx) return fromCtx
      const fromQuery = this.queryStr('canvas')
      if (fromQuery) return fromQuery
      try {
        return String(sessionStorage.getItem(NGC_LAST_OPENED_CANVAS_KEY) || '').trim()
      } catch {
        return ''
      }
    },
    buildWorkspaceReturnQuery(extra) {
      const ctx = this.readDirectorReturnCtx()
      const deck = (ctx && ctx.deck) || this.queryStr('deck')
      const img = (ctx && ctx.img) || this.queryStr('img')
      const canvas = this.resolveReturnCanvasId()
      const wq = Object.assign(
        {
          node: '1',
          focusNode: String(img || deck || '').trim()
        },
        extra && typeof extra === 'object' ? extra : {}
      )
      if (canvas) wq.canvas = canvas
      if (!wq.focusNode && deck) wq.focusNode = String(deck).trim()
      return wq
    },
    goBackFromDirector() {
      const q = this.$route.query || {}
      if (q.ngc === '1' || q.ngc === 1) {
        const wq = this.buildWorkspaceReturnQuery({ ngcFocus: '1', ngcDirectorScene: '1' })
        if (wq.focusNode) {
          /* keep */
        } else {
          delete wq.ngcFocus
        }
        this.$router.push({ path: '/workspace', query: wq }).catch(() => {})
        return
      }
      this.$router.push('/home').catch(() => {})
    },
    dataUrlToBlob(dataUrl) {
      const s = String(dataUrl || '')
      const m = /^data:([^;]+);base64,(.+)$/i.exec(s)
      if (!m) return null
      try {
        const bin = atob(m[2])
        const len = bin.length
        const arr = new Uint8Array(len)
        for (let i = 0; i < len; i++) arr[i] = bin.charCodeAt(i)
        return new Blob([arr], { type: m[1] || 'image/png' })
      } catch {
        return null
      }
    },
    async handleCapturesFromDesk(captures) {
      const list = (captures || []).filter(c => c && String(c.dataUrl || '').trim())
      if (!list.length) {
        showErrorToast('未收到截图')
        return
      }

      if (!this.isOpenedFromNodeGraph) {
        // 非画布入口：逐张下载
        let ok = 0
        for (let i = 0; i < list.length; i++) {
          const dataUrl = String(list[i].dataUrl).trim()
          const fileName = String(list[i].fileName || `director-desk-${Date.now()}-${i + 1}.png`).trim()
          try {
            const a = document.createElement('a')
            a.href = dataUrl
            a.download = fileName
            a.rel = 'noopener'
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            ok += 1
          } catch {
            /* continue */
          }
        }
        if (ok) showSuccessToast(ok > 1 ? `已下载 ${ok} 张截图` : '已下载截图')
        else showErrorToast('下载失败')
        return
      }

      this.busy = `正在回写画布…(0/${list.length})`
      const ctx = this.readDirectorReturnCtx()
      const deck = String((ctx && ctx.deck) || this.queryStr('deck') || '').trim()
      const canvas = this.resolveReturnCanvasId()
      const wq = this.buildWorkspaceReturnQuery({ ngcDirectorShot: '1', ngcDirectorScene: '1' })
      wq.focusNode = deck || wq.focusNode || ''

      const finishNavigate = () => {
        if (canvas) {
          try {
            sessionStorage.setItem(NGC_LAST_OPENED_CANVAS_KEY, canvas)
          } catch {
            /* ignore */
          }
        }
        setTimeout(() => {
          this.busy = ''
          this.$router.replace({ path: '/workspace', query: wq }).catch(() => {
            this.$router.push({ path: '/workspace', query: wq }).catch(() => {})
          })
        }, 80)
      }

      const items = []
      for (let i = 0; i < list.length; i++) {
        const dataUrl = String(list[i].dataUrl).trim()
        const fileName = String(list[i].fileName || `director-desk-${i + 1}.png`).trim()
        this.busy = `正在上传截图 ${i + 1}/${list.length}…`
        try {
          const imageUrl = await uploadVideoImage({
            image: dataUrl,
            title: fileName.replace(/\.png$/i, '') || `导演台截图${i + 1}`
          })
          const u = imageUrl != null ? String(imageUrl).trim() : ''
          if (u && (/^https?:\/\//i.test(u) || u.startsWith('/'))) {
            items.push({ imageUrl: u, fileName })
            continue
          }
          throw new Error('上传未返回有效 URL')
        } catch (e) {
          console.warn('[DirectorDeckHost] capture upload failed, keep dataUrl', e)
          items.push({ dataUrl, fileName })
        }
      }

      if (!items.length) {
        this.busy = ''
        showErrorToast('截图回写失败')
        return
      }

      const first = items[0]
      const payload = {
        v: 4,
        items,
        deckId: deck || '',
        canvasId: canvas || '',
        imageNodeId: '',
        // 兼容旧版单图读取
        ...(first.imageUrl
          ? { imageUrl: first.imageUrl }
          : first.dataUrl
            ? { dataUrl: first.dataUrl }
            : {})
      }

      try {
        sessionStorage.setItem(NGC_DIRECTOR_PNG_SESSION_KEY, JSON.stringify(payload))
        showSuccessToast(
          items.length > 1 ? `已准备 ${items.length} 张截图，正在回写画布` : '已回写画布'
        )
        finishNavigate()
      } catch (e) {
        console.warn('[DirectorDeckHost] sessionStorage failed for multi captures', e)
        // 过大：仅保留已上传的 URL 项
        const urlOnly = items.filter(x => x.imageUrl)
        if (urlOnly.length) {
          try {
            sessionStorage.setItem(
              NGC_DIRECTOR_PNG_SESSION_KEY,
              JSON.stringify({
                v: 4,
                items: urlOnly,
                deckId: deck || '',
                canvasId: canvas || '',
                imageNodeId: '',
                imageUrl: urlOnly[0].imageUrl
              })
            )
            showSuccessToast(`已回写 ${urlOnly.length} 张（已上传）`)
            finishNavigate()
            return
          } catch {
            /* fall through */
          }
        }
        this.busy = ''
        delete wq.ngcDirectorShot
        wq.ngcFocus = '1'
        showErrorToast('截图过多无法缓存，请减少后重试或检查网络上传')
        this.$router.push({ path: '/workspace', query: wq }).catch(() => {})
      }
    }
  }
}
</script>
